// DepositHint — small banner that shows up beneath the deposit input on
// the Reservations and Check In / Out forms when the system-wide
// "deposit.required" policy is on.  Reads live from SettingsContext, so
// flipping the switch in Settings reflects everywhere on the next
// render without a page reload.
//
// Props:
//   total : the booking's grand total (number) — used to soften the
//           required minimum on cheaper stays (we never ask for more
//           than the bill itself).
//   paid  : the amount the operator has typed so far (number).
//
// Exports:
//   <DepositHint total={…} paid={…} />          UI banner
//   useDepositGuard({ total, paid }) → { ok, required, message }
//                                              imperative guard for
//                                              submit handlers.

import React from "react";
import { useSettings } from "../context/SettingsContext";

function computeRequired(total, settings) {
  if (!settings["deposit.required"]) return 0;
  const min = Number(settings["deposit.min_amount"]) || 0;
  const t   = Math.max(0, Number(total) || 0);
  // Never require more than the booking itself.
  return Math.min(min, t);
}

export function useDepositGuard({ total, paid }) {
  const { settings } = useSettings();
  const required = computeRequired(total, settings);
  const ok = required <= 0 || (Number(paid) || 0) >= required;
  const message = required > 0 && !ok
    ? `Deposit policy: collect at least GH₵ ${required.toFixed(2)} before saving.`
    : "";
  return { ok, required, message, enabled: !!settings["deposit.required"] };
}

export default function DepositHint({ total, paid }) {
  const { settings } = useSettings();
  if (!settings["deposit.required"]) return null;

  const required = computeRequired(total, settings);
  const paidNum  = Number(paid) || 0;
  const ok = required <= 0 || paidNum >= required;

  return (
    <div style={{
      marginTop: 6,
      padding: "10px 12px",
      borderRadius: 8,
      background: ok ? "rgba(74,124,89,.08)" : "rgba(192,57,43,.07)",
      border: `1px solid ${ok ? "rgba(74,124,89,.3)" : "rgba(192,57,43,.3)"}`,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: 10, flexWrap: "wrap",
      fontSize: 12.5, fontWeight: 500,
    }}>
      <div style={{ color: ok ? "#2d6a4f" : "#a93226" }}>
        {ok ? "✓ Deposit policy satisfied" : "⚠️ Deposit policy"}
      </div>
      <div style={{ color: "#3a2a1a", fontWeight: 400 }}>
        Required:{" "}
        <strong style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontWeight: 600 }}>
          GH₵ {required.toFixed(2)}
        </strong>
        {" "}· Collected:{" "}
        <strong style={{
          fontFamily: "'Cormorant Garamond', serif", fontSize: 15, fontWeight: 600,
          color: ok ? "#2d6a4f" : "#a93226",
        }}>
          GH₵ {paidNum.toFixed(2)}
        </strong>
      </div>
    </div>
  );
}
